/**
 * useImagePreloader Hook
 * Intelligently preloads images for cards in/near the viewport
 * Uses Intersection Observer for efficient viewport detection
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { cacheImages } from '@/services/imageCacheService';

interface UseImagePreloaderOptions {
  /**
   * Number of cards ahead/behind viewport to preload
   * Default: 2
   */
  preloadMargin?: number;
  /**
   * Root margin for intersection observer
   * Default: '200px' (preload when card is 200px away from viewport)
   */
  rootMargin?: string;
  /**
   * Maximum number of images to preload in parallel
   * Default: 5
   */
  maxConcurrent?: number;
  /**
   * Whether to respect reduced data mode
   * Default: true
   */
  respectDataSaver?: boolean;
}

/**
 * Hook to preload images for experience cards
 * @param imageUrls Array of image URLs to preload
 * @param options Configuration options
 */
export function useImagePreloader(
  imageUrls: string[],
  options: UseImagePreloaderOptions = {}
) {
  const { maxConcurrent = 5, respectDataSaver = true } = options;

  const [preloadedUrls, setPreloadedUrls] = useState<Set<string>>(new Set());
  const observerRef = useRef<IntersectionObserver | null>(null);
  const preloadingRef = useRef<Set<string>>(new Set());
  const queueRef = useRef<string[]>([]);
  const activePreloadsRef = useRef<number>(0);

  // Check if data saver mode is enabled
  const isDataSaverEnabled = (): boolean => {
    if (!respectDataSaver || typeof navigator === 'undefined') {
      return false;
    }
    // @ts-expect-error - connection.saveData may not be in types
    return navigator.connection?.saveData === true;
  };

  // Process preload queue
  const processQueue = async () => {
    if (isDataSaverEnabled()) {
      return; // Don't preload in data saver mode
    }

    while (
      queueRef.current.length > 0 &&
      activePreloadsRef.current < maxConcurrent
    ) {
      const url = queueRef.current.shift();
      if (!url || preloadingRef.current.has(url)) {
        continue;
      }

      preloadingRef.current.add(url);
      activePreloadsRef.current++;

      // Preload in background
      cacheImages([url])
        .then(results => {
          if (results[0]) {
            setPreloadedUrls(prev => new Set(prev).add(url));
          }
        })
        .catch(error => {
          console.warn('[useImagePreloader] Failed to preload image:', error);
        })
        .finally(() => {
          preloadingRef.current.delete(url);
          activePreloadsRef.current--;
          // Process next item in queue
          processQueue();
        });
    }
  };

  // Preload images immediately (for priority images)
  const preloadImmediate = async (urls: string[]) => {
    if (isDataSaverEnabled() || urls.length === 0) {
      return;
    }

    const urlsToPreload = urls.filter(
      url => !preloadingRef.current.has(url) && !preloadedUrls.has(url)
    );

    if (urlsToPreload.length === 0) return;

    // Add to queue
    queueRef.current.push(...urlsToPreload);
    processQueue();
  };

  // Cleanup on unmount
  useEffect(() => {
    const observer = observerRef.current;
    return () => {
      if (observer) {
        observer.disconnect();
      }
    };
  }, []);

  return {
    preloadedUrls,
    preloadImmediate,
    isDataSaverEnabled: isDataSaverEnabled(),
  };
}

/**
 * Hook to preload images for a specific card element
 * Uses Intersection Observer to detect when card enters viewport
 */
export function useCardImagePreloader(
  cardRef: React.RefObject<HTMLElement>,
  imageUrls: string[],
  options: UseImagePreloaderOptions = {}
) {
  const {
    rootMargin = '200px',
    maxConcurrent = 5,
    respectDataSaver = true,
  } = options;

  const [isInViewport, setIsInViewport] = useState(false);
  const [hasPreloaded, setHasPreloaded] = useState(false);
  const preloadingRef = useRef<Set<string>>(new Set());
  const hasPreloadedRef = useRef(false);

  // Check if data saver mode is enabled
  const isDataSaverEnabled = useCallback((): boolean => {
    if (!respectDataSaver || typeof navigator === 'undefined') {
      return false;
    }
    // @ts-expect-error - connection.saveData may not be in types
    return navigator.connection?.saveData === true;
  }, [respectDataSaver]);

  // Preload images when card enters viewport
  useEffect(() => {
    if (!cardRef.current || imageUrls.length === 0 || hasPreloadedRef.current) {
      return;
    }

    if (isDataSaverEnabled()) {
      return; // Don't preload in data saver mode
    }

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !hasPreloadedRef.current) {
            setIsInViewport(true);
            setHasPreloaded(true);
            hasPreloadedRef.current = true;

            // Preload images
            const urlsToPreload = imageUrls.filter(
              url => !preloadingRef.current.has(url)
            );

            if (urlsToPreload.length > 0) {
              urlsToPreload.forEach(url => {
                preloadingRef.current.add(url);
              });

              // Preload in batches to respect maxConcurrent
              const batches: string[][] = [];
              for (let i = 0; i < urlsToPreload.length; i += maxConcurrent) {
                batches.push(urlsToPreload.slice(i, i + maxConcurrent));
              }

              batches.forEach(batch => {
                cacheImages(batch).catch(error => {
                  console.warn(
                    '[useCardImagePreloader] Failed to preload images:',
                    error
                  );
                });
              });
            }
          }
        });
      },
      {
        rootMargin,
        threshold: 0.1,
      }
    );

    observer.observe(cardRef.current);

    return () => {
      observer.disconnect();
    };
  }, [
    cardRef,
    imageUrls,
    rootMargin,
    maxConcurrent,
    respectDataSaver,
    isDataSaverEnabled,
  ]);

  return {
    isInViewport,
    hasPreloaded,
    isDataSaverEnabled: isDataSaverEnabled(),
  };
}
